import { useEffect, useMemo } from 'react';

import { Typography } from '@remarkable/ark-web';
import { useLocation, useNavigate } from '@tanstack/react-router';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import {
  useActivePaperTablets,
  useHasSubscription,
  useSubscription,
} from 'src/api/queries';
import { Feedback } from 'src/components';
import { AppPage } from 'src/components/AppPage';
import { ChangePaymentIntervalBanners } from 'src/components/ChangePaymentIntervalBanners';
import { PairingCompleteNotification } from 'src/components/PairingCompleteNotification';
import { UpdatePaymentCardBanner } from 'src/components/UpdatePaymentCardBanner';
import { UxSignals } from 'src/components/UxSignals/UxSignals';
import { TestId } from 'src/testids';

import { AppsAndExtensions } from './components/AppsAndExtensions';
import { DropZone } from './components/DropZone/DropZone';
import { Hero } from './components/Hero';
import { MethodsCard } from './components/MethodsCard';
import { PasscodeReset } from './components/PasscodeReset';
import { PausedSubscriptionBanner } from './components/PausedSubscriptionBanner';

import { Trans, useLingui } from '@lingui/react/macro';
import { useFeatureFlags } from 'src/utils/useFeatureFlags';
import { BrowserExtensionCard } from './components/BrowserExtensionCard';
import { MeetingNotesCard } from './components/MeetingNotesCard';

export const Home = () => {
  const featureFlags = useFeatureFlags();
  const subscription = useSubscription();
  const hasSubscription = useHasSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLingui();
  // TODO: Move this to top level of app when we move email verified out
  useEffect(() => {
    if (location.hash.includes('browser')) {
      void navigate({ to: '/device/browser/connect' });
      return;
    }

    if (location.hash.includes('mobile')) {
      void navigate({ to: '/device/mobile/connect' });
      return;
    }

    if (location.hash.includes('desktop')) {
      void navigate({ to: '/device/desktop/connect' });
      return;
    }
  }, [location.hash]);

  const showPairingComplete = useMemo(() => {
    const params = new URLSearchParams(location.searchStr);
    return params.get('flow') === 'pairing-complete';
  }, [location.searchStr]);

  const activePaperTablets = useActivePaperTablets();

  const hasDevice = !!activePaperTablets.data?.length;

  return (
    <AppPage.Container data-cy={TestId.PageHome}>
      <AppPage.Content className="max-w-l2xl text-center">
        {showPairingComplete && <PairingCompleteNotification />}

        <PasscodeReset />
        <ChangePaymentIntervalBanners
          subscription={subscription.data}
          componentLocation={ComponentLocations.NOTIFICATIONS.FRONT_PAGE}
        />
        <UpdatePaymentCardBanner subscription={subscription} />
        <PausedSubscriptionBanner subscription={subscription.data} />

        <div className="mt-32 flex w-full flex-col gap-32 lm:gap-64 lxl:mt-48 l2xl:mt-64">
          <Hero />
          {hasDevice && !featureFlags.EnableLinkToNewWebApp && (
            <div className="flex flex-col gap-32 text-left">
              <Typography variant="heading-sm" as="h2">
                <Trans>Upload files to your reMarkable</Trans>
              </Typography>
              <DropZone />
            </div>
          )}

          <UxSignals />
          <MeetingNotesCard hasSubscription={hasSubscription.data} />
          <BrowserExtensionCard hasSubscription={hasSubscription.data} />
          <MethodsCard hasSubscription={hasSubscription.data} />

          <AppsAndExtensions />

          <Feedback
            title={t`Did you find what you were looking for?`}
            feedbackTargetId={'home-page-v1'}
          />
        </div>
      </AppPage.Content>
    </AppPage.Container>
  );
};
